"use client";
import { useParams } from 'next/navigation';
import { createContext, useContext, useReducer, Dispatch } from 'react';
import enJson from "@/app/locales/english.json";
import chJson from "@/app/locales/chinese.json";
import dayJS from './DayJS';
import 'dayjs/locale/zh-hk';
import 'dayjs/locale/en';

export const LANGTYPE_EN = "english";
export const LANGTYPE_CT = "chinese";

export const LANGCODE_EN = "en-us";
export const LANGCODE_CT = "zh-hk";

export type LangType = typeof LANGTYPE_EN | typeof LANGTYPE_CT;
export type LangCode = typeof LANGCODE_EN | typeof LANGCODE_CT;

type Language = {
  [code in LangCode]: {
    display: string;
    type: LangType;
    json: any;
    locale: string;
  };
};

export const LANGUAGE: Language  = {
  [LANGCODE_EN]: {
    display: "English",
    type: LANGTYPE_EN,
    json: enJson,
    locale: "en",
  },
  [LANGCODE_CT]: {
    display: "中文",
    type: LANGTYPE_CT,
    json: chJson,
    locale: "zh-hk",
  }
};

export const translation = (langCode: LangCode, key: string, defaultValue?: string) => (LANGUAGE[langCode]?.json as any)?.[key] || defaultValue || key
export const str2LangCode = (language: string):LangCode => language === LANGCODE_CT ? LANGCODE_CT : LANGCODE_EN;

interface LanguageState {
  langTitle: string;  // for display in header
  langType: LangType;  // cuurent lang
  langCode: LangCode;  // current lang code
  t: (key: string, defaultValue?: string) => string // translation
}

interface LanguageContext {
  state: LanguageState
  dispatch: Dispatch<LangCode>
}

const languageReducer = (language: LanguageState, code: LangCode): LanguageState =>  {
  dayJS.locale(LANGUAGE[code].locale);
  return {
    langTitle: LANGUAGE[code].display,
    langType: LANGUAGE[code].type,
    langCode: code,
    t: (key:string, defaultValue?:string) => translation(code, key, defaultValue)
  };
}

export const LanguageContext = createContext<LanguageContext>({} as LanguageContext);

interface LangProviderProps {
  children?: React.ReactNode;
}

export function LanguageProvider({ children }: LangProviderProps) {  
  const {language} = useParams();
  console.log(language, 'language');
  let currentlanguage: LanguageState = {} as LanguageState;

  const langCode: LangCode = str2LangCode(language?.toString() ?? "en-us");
  dayJS.locale(LANGUAGE[langCode].locale);
  currentlanguage = {
    langTitle: LANGUAGE[langCode].display,
    langType: LANGUAGE[langCode].type,
    langCode: langCode,
    t: (key:string, defaultValue?:string) => translation(langCode, key, defaultValue)
  }

  const [state, dispatch] = useReducer(
    languageReducer,
    currentlanguage
  );

  const value: LanguageContext = {state, dispatch}

  return (
    <LanguageContext.Provider value={value}>
      <html lang={LANGUAGE[langCode].locale}>{children}</html>
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  return useContext(LanguageContext);
}